import SEO from "@/components/SEO";
import { PageHero, Section } from "@/components/sections";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/constants/contact";

const LAST_UPDATED = "14 September 2026";

export default function Privacy() {
  return (
    <div>
      <SEO
        path="/privacy"
        title="Privacy Policy"
        description="How Rao Investment Solutions collects, uses, stores and protects your personal data, and the rights you have over it under India's Digital Personal Data Protection Act, 2023."
      />
      <PageHero
        label="Legal"
        title="Privacy Policy"
        intro="What we collect, why we collect it, who we share it with, and the control you keep over it."
        breadcrumbs={[{ name: "Home", to: "/" }, { name: "Privacy Policy" }]}
      />

      <Section className="!pt-0">
        <div className="max-w-3xl">
          <p className="font-data text-xs text-slate mb-12">Last updated: {LAST_UPDATED}</p>

          <Clause title="Who we are">
            <p>
              Rao Investment Solutions ("RIS", "we", "us") is a sole proprietorship operating under the
              legal entity Rao Enterprises, an AMFI-registered Mutual Fund Distributor holding{" "}
              <strong className="text-navy">{CONTACT.arn}</strong>. We are the data fiduciary responsible
              for the personal data described in this policy.
            </p>
            <p>
              This policy explains how we handle personal data under India's Digital Personal Data
              Protection Act, 2023 (the "DPDP Act") and applicable SEBI and AMFI guidance.
            </p>
          </Clause>

          <Clause title="What we collect">
            <p>We only collect what you choose to give us. Specifically:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong className="text-navy">Details you submit</strong> — your name, email address,
                phone number, the areas you tell us you are interested in, and any message you write, when
                you fill in an enquiry or callback form on this site.
              </li>
              <li>
                <strong className="text-navy">How you reached us</strong> — the page you submitted from
                and campaign tags in the link you arrived through (commonly called UTM parameters), so we
                know which of our pages or campaigns brought you here.
              </li>
              <li>
                <strong className="text-navy">Calculator inputs</strong> — if you submit an enquiry from a
                calculator, the figures you entered may accompany it so we can respond in context. The
                calculators themselves run entirely in your browser; simply using one sends us nothing.
              </li>
            </ul>
            <p>
              We do <strong className="text-navy">not</strong> collect PAN, Aadhaar, bank account details,
              demat details or any financial credentials through this website. Any such information needed
              to open an investment account is collected later, separately, through the regulated
              onboarding process of the relevant AMC, RTA, exchange or platform — never by email or through
              a form on this site.
            </p>
          </Clause>

          <Clause title="Why we use it">
            <p>We use your personal data only to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>respond to your enquiry and contact you about it, by phone, email or WhatsApp;</li>
              <li>understand your goals well enough to advise you properly;</li>
              <li>send you service communications relating to an engagement you have with us;</li>
              <li>meet our record-keeping obligations as an AMFI-registered distributor.</li>
            </ul>
            <p>
              We do not sell your personal data. We do not share it with advertisers or data brokers, and
              we do not use it to build advertising profiles.
            </p>
          </Clause>

          <Clause title="Consent, and withdrawing it">
            <p>
              When you submit a form on this site, you consent to us contacting you about your enquiry.
              That consent is specific to that purpose, and it is not permanent.
            </p>
            <p>
              You can withdraw it at any time by emailing{" "}
              <a href={`mailto:${CONTACT.email}`} className="text-orange hover:underline">
                {CONTACT.email}
              </a>{" "}
              or replying "STOP" to any message from us. We will stop contacting you for marketing
              purposes promptly. Withdrawing consent does not affect anything done lawfully before you
              withdrew it, and we may still need to retain certain records where regulation requires it.
            </p>
            <p>
              If you are an existing client, we may still send you communications necessary to service your
              investments — these are not marketing, and they continue for as long as the engagement does.
            </p>
          </Clause>

          <Clause title="Who we share it with">
            <p>We share your personal data only with:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong className="text-navy">Our CRM provider (Zoho)</strong> — enquiries submitted
                through this site are stored in Zoho CRM, which holds the data on our behalf under its own
                contractual and security obligations.
              </li>
              <li>
                <strong className="text-navy">Product providers</strong> — where you ask us to act, such as
                an AMC, registrar and transfer agent, PMS or AIF manager, insurer or exchange
                intermediary, and only to the extent needed to execute what you have asked for.
              </li>
              <li>
                <strong className="text-navy">Regulators and authorities</strong> — where we are legally
                required to disclose, including to SEBI, AMFI or a competent authority under due process.
              </li>
            </ul>
          </Clause>

          <Clause title="How long we keep it">
            <p>
              Enquiries that do not become engagements are retained for up to three years, so we can pick
              up a conversation where it left off. Where you become a client, records are kept for the
              period required under applicable SEBI and AMFI record-keeping rules, and for as long as is
              needed to service the relationship.
            </p>
          </Clause>

          <Clause title="How we protect it">
            <p>
              This site is served over HTTPS, and enquiry data is held in access-controlled systems limited
              to those who need it to respond to you. No system is perfectly secure, and we cannot guarantee
              absolute security, but we take reasonable technical and organisational measures appropriate to
              the sensitivity of the data.
            </p>
            <p>
              We will never ask you for a password, OTP, PIN or bank credential — by email, phone or any
              other channel. If you receive such a request claiming to be from us, it is not from us.
              Please report it to{" "}
              <a href={`mailto:${CONTACT.email}`} className="text-orange hover:underline">
                {CONTACT.email}
              </a>
              .
            </p>
          </Clause>

          <Clause title="Your rights">
            <p>Under the DPDP Act you have the right to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>ask what personal data of yours we hold, and why;</li>
              <li>have inaccurate or incomplete data corrected or completed;</li>
              <li>have your data erased, where we are not required to retain it;</li>
              <li>withdraw your consent, as described above;</li>
              <li>nominate someone to exercise these rights on your behalf if you are unable to;</li>
              <li>raise a grievance with us, and escalate to the Data Protection Board of India.</li>
            </ul>
            <p>
              To exercise any of these, write to{" "}
              <a href={`mailto:${CONTACT.email}`} className="text-orange hover:underline">
                {CONTACT.email}
              </a>
              . We aim to respond within 30 days.
            </p>
          </Clause>

          <Clause title="Cookies and analytics">
            <p>
              This site does not set advertising or tracking cookies, and does not run third-party
              behavioural analytics. Any embedded third-party form (such as our CRM enquiry form) may set
              cookies strictly necessary for that form to function, governed by that provider's own privacy
              policy.
            </p>
          </Clause>

          <Clause title="Children">
            <p>
              This site is not directed at children, and we do not knowingly collect the personal data of
              anyone under 18. If you believe a child has submitted data to us, contact us and we will
              delete it.
            </p>
          </Clause>

          <Clause title="Changes to this policy">
            <p>
              We may update this policy as our practices or the law change. The revised version takes
              effect when posted here, and the "last updated" date above will change. Material changes
              affecting how we use data you have already given us will be notified to you directly where we
              hold contact details for you.
            </p>
          </Clause>

          <Clause title="Contact and grievances">
            <p>
              For any question about this policy, or to raise a grievance about how your data has been
              handled, contact:
            </p>
            <div className="mt-4 p-6 bg-cream border border-navy/10 font-sans text-sm not-italic">
              <div className="font-serif text-xl text-navy">Karthik Rao</div>
              <div className="text-slate mt-1">Grievance Officer, Rao Investment Solutions</div>
              <div className="mt-4 space-y-1 font-data text-sm">
                <div>
                  <a href={`mailto:${CONTACT.email}`} className="text-navy hover:text-orange transition-colors">
                    {CONTACT.email}
                  </a>
                </div>
                <div>
                  <a href={CONTACT.phoneHref} className="text-navy hover:text-orange transition-colors">
                    {CONTACT.phoneDisplay}
                  </a>
                </div>
                <div className="text-slate">{CONTACT.officeShort}</div>
              </div>
            </div>
            <p className="mt-6">
              If your grievance is not resolved to your satisfaction, you may escalate it to the Data
              Protection Board of India.
            </p>
          </Clause>

          <div className="mt-16 pt-8 border-t border-navy/10">
            <p className="font-data text-xs text-slate leading-relaxed">
              Rao Investment Solutions is an AMFI-registered Mutual Fund Distributor ({CONTACT.arn}).
              Mutual fund investments are subject to market risks. Read all scheme related documents
              carefully before investing. This policy governs personal data only; it is not investment
              advice and does not form part of any investment agreement.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
}

const Clause = ({ title, children }) => (
  <Reveal>
    <section className="mb-12">
      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight">{title}</h2>
      <div className="mt-4 space-y-4 font-sans text-base text-slate leading-relaxed">{children}</div>
    </section>
  </Reveal>
);
